import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './PiggyBankWithdrawModal.css';
import SoundManager from '../services/SoundManager';
import api from '../services/api';

// Данные о APR для модалки вывода
interface APRPreviewData {
  hasStaking: boolean;
  currentAPR: number;
  initialAPR: number;
  bonusAPR: number;
  accumulatedAPR: number;
  newAPR: number;
  lossAPR: number;
}

type CurrencyType = 'SPERM' | 'USDT' | 'SLOTS';

// Данные успешного перевода в слоты
interface SlotsTransferResult {
  transferred: number;
  wagerRequired: number;
  wagerProgress: number;
  newPiggyBalance: number;
  newMainBalance: number;
}

interface PiggyBankWithdrawModalProps {
  isOpen: boolean;
  onClose: () => void;
  balance: number;
  balanceUsdt: number; // Баланс USDT в копилке
  aprPreview: APRPreviewData | null;
  onConfirm: (amount: number | null) => void;
  onSuccess?: () => void; // Callback после успешного вывода
}

const PiggyBankWithdrawModal: React.FC<PiggyBankWithdrawModalProps> = ({
  isOpen,
  onClose,
  balance,
  balanceUsdt,
  aprPreview,
  onConfirm,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [withdrawAmount, setWithdrawAmount] = useState<string>('');
  const [withdrawAll, setWithdrawAll] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyType>('SPERM');
  const [isWithdrawingUsdt, setIsWithdrawingUsdt] = useState<boolean>(false);
  const [isTransferringToSlots, setIsTransferringToSlots] = useState<boolean>(false);
  const [slotsTransferSuccess, setSlotsTransferSuccess] = useState<SlotsTransferResult | null>(null);

  useEffect(() => {
    if (isOpen) {
      SoundManager.playModalOpen();
      setWithdrawAmount('');
      setWithdrawAll(true);
      setError('');
      setSelectedCurrency('SPERM');
      setIsWithdrawingUsdt(false);
      setIsTransferringToSlots(false);
      setSlotsTransferSuccess(null);
    }
  }, [isOpen]);

  // Текущий баланс в зависимости от выбранной валюты
  const currentBalance = selectedCurrency === 'SLOTS' ? balance : (selectedCurrency === 'SPERM' ? balance : balanceUsdt);

  if (!isOpen) return null;

  const handleAmountChange = (value: string) => {
    // Разрешаем числа и точку для USDT
    if (selectedCurrency === 'USDT') {
      if (!/^\d*\.?\d*$/.test(value)) return;
    } else {
      if (!/^\d*$/.test(value)) return;
    }

    setWithdrawAmount(value);
    setWithdrawAll(false);
    setError('');

    const numValue = parseFloat(value) || 0;

    if (numValue <= 0) {
      setError(t('piggyBankWithdraw.errorAmountZero'));
    } else if (numValue > currentBalance) {
      setError(t('piggyBankWithdraw.errorInsufficientFunds'));
    }
  };

  const handleWithdrawAll = () => {
    setWithdrawAll(true);
    setWithdrawAmount(currentBalance.toString());
    setError('');
  };

  // Переключение валюты
  const handleCurrencyChange = (currency: CurrencyType) => {
    setSelectedCurrency(currency);
    setWithdrawAmount('');
    setWithdrawAll(true);
    setError('');
  };

  // Хаптик-вибрация через Telegram WebApp
  const triggerHaptic = (type: string = 'success') => {
    try {
      const tg = (window as any).Telegram?.WebApp;
      if (tg?.HapticFeedback) {
        if (type === 'success') {
          tg.HapticFeedback.notificationOccurred('success');
        } else {
          tg.HapticFeedback.impactOccurred('medium');
        }
      }
    } catch (e) { /* ignore */ }
  };

  // Перевод в слоты (без APR сброса, с вейджером)
  const handleTransferToSlots = async () => {
    if (isTransferringToSlots) return;

    setIsTransferringToSlots(true);
    try {
      const amount = withdrawAll ? null : parseFloat(withdrawAmount);
      const response = await api.post('/piggybank/transfer-to-slots', { amount });

      if (response.data.success) {
        const data = response.data.data;
        const wl = data.wagerLock;

        // Вибрация успеха
        triggerHaptic('success');
        SoundManager.playModalOpen();

        // Показываем красивый экран успеха
        setSlotsTransferSuccess({
          transferred: data.transferred,
          wagerRequired: wl.required,
          wagerProgress: wl.progress,
          newPiggyBalance: data.newPiggyBalance,
          newMainBalance: data.newMainBalance
        });

        onSuccess?.();
      }
    } catch (error: any) {
      console.error('Ошибка перевода в слоты:', error);
      triggerHaptic('medium');
      setError(error.response?.data?.message || t('piggyBankWithdraw.slotsTransferError'));
    } finally {
      setIsTransferringToSlots(false);
    }
  };

  // Вывод USDT из копилки
  const handleWithdrawUsdt = async () => {
    if (isWithdrawingUsdt) return;

    setIsWithdrawingUsdt(true);
    try {
      const amount = withdrawAll ? null : parseFloat(withdrawAmount);
      const response = await api.post('/piggybank/withdraw-usdt', { amount });

      if (response.data.success) {
        const data = response.data.data;
        alert(`✅ ${data.message}\n\n${t('piggyBankWithdraw.newUsdtBalance')}: ${data.newUsdtBalance} USDT`);
        onSuccess?.();
        onClose();
      }
    } catch (error: any) {
      console.error('Ошибка вывода USDT из копилки:', error);
      alert(error.response?.data?.message || t('piggyBankWithdraw.usdtWithdrawError'));
    } finally {
      setIsWithdrawingUsdt(false);
    }
  };

  const handleConfirm = () => {
    // Для USDT используем отдельную функцию
    if (selectedCurrency === 'USDT') {
      handleWithdrawUsdt();
      return;
    }

    // Для SLOTS - перевод в слоты без APR сброса
    if (selectedCurrency === 'SLOTS') {
      handleTransferToSlots();
      return;
    }

    // Для SPERM - стандартная логика
    if (withdrawAll) {
      onConfirm(null); // null означает "вывести всё"
    } else {
      const numAmount = parseInt(withdrawAmount) || 0;
      if (numAmount <= 0 || numAmount > balance) {
        return;
      }
      onConfirm(numAmount);
    }
  };

  const finalAmount = withdrawAll ? currentBalance : (parseFloat(withdrawAmount) || 0);

  // Есть потеря APR только для SPERM, если есть стейкинг и накопленный APR > 0
  // USDT вывод НЕ сбрасывает APR!
  const hasAPRLoss = selectedCurrency === 'SPERM' && aprPreview?.hasStaking && (aprPreview?.accumulatedAPR ?? 0) > 0;
  const currentAPR = aprPreview?.currentAPR || 1.0;
  const newAPR = aprPreview?.newAPR || 1.0;
  const lossAPR = aprPreview?.lossAPR || 0;
  const accumulatedAPR = aprPreview?.accumulatedAPR || 0;
  const bonusAPR = aprPreview?.bonusAPR || 0;

  // Экран успеха — красивая анимация после перевода
  if (slotsTransferSuccess) {
    const pct = slotsTransferSuccess.wagerRequired > 0
      ? Math.min(100, (slotsTransferSuccess.wagerProgress / slotsTransferSuccess.wagerRequired) * 100)
      : 0;

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content piggybank-withdraw-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-body" style={{ textAlign: 'center', padding: '32px 24px' }}>
            {/* Анимированная галочка */}
            <div className="slots-success-icon">
              <div className="slots-success-circle">
                <svg className="slots-success-checkmark" viewBox="0 0 52 52">
                  <circle className="slots-success-circle-bg" cx="26" cy="26" r="25" fill="none"/>
                  <path className="slots-success-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
                </svg>
              </div>
            </div>

            {/* Сумма перевода */}
            <div className="slots-success-amount">
              +{slotsTransferSuccess.transferred.toFixed(2)} SPERM
            </div>
            <div className="slots-success-label">
              {t('piggyBankWithdraw.transferredToSlots', 'Переведено на игровой баланс')}
            </div>

            {/* APR сохранён */}
            <div className="slots-success-apr-badge">
              <span>✅</span> APR {t('piggyBankWithdraw.aprSaved', 'сохранён!')}
            </div>

            {/* Инфо о вейджере */}
            <div className="slots-success-wager-block">
              <div className="slots-success-wager-title">
                🔒 {t('piggyBankWithdraw.wagerCondition', 'Условие разблокировки')}
              </div>
              <div className="slots-success-wager-bar-container">
                <div className="slots-success-wager-bar">
                  <div
                    className="slots-success-wager-fill"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="slots-success-wager-text">
                  {slotsTransferSuccess.wagerProgress.toFixed(0)} / {slotsTransferSuccess.wagerRequired.toFixed(0)} SPERM
                </div>
              </div>
              <div className="slots-success-wager-hint">
                {t('piggyBankWithdraw.wagerHint', 'Делайте ставки в слотах — средства разблокируются постепенно')}
              </div>
            </div>

            {/* Пожелание */}
            <div className="slots-success-wish">
              🎰 {t('piggyBankWithdraw.goodLuckWish', 'Удачной игровой сессии!')} 🍀
            </div>

            {/* Кнопка закрыть */}
            <button
              className="btn-confirm slots-btn"
              style={{ width: '100%', marginTop: '20px' }}
              onClick={onClose}
            >
              {t('common.close', 'Закрыть')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content piggybank-withdraw-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🐷 {t('piggyBankWithdraw.title')}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {/* Табы выбора валюты */}
          <div className="currency-tabs">
            <button
              className={`currency-tab ${selectedCurrency === 'SPERM' ? 'active' : ''}`}
              onClick={() => handleCurrencyChange('SPERM')}
            >
              🪙 SPERM
            </button>
            <button
              className={`currency-tab ${selectedCurrency === 'SLOTS' ? 'active' : ''}`}
              onClick={() => handleCurrencyChange('SLOTS')}
              disabled={balance <= 0}
            >
              🎰 {t('piggyBankWithdraw.toSlots', 'В слоты')}
            </button>
            <button
              className={`currency-tab ${selectedCurrency === 'USDT' ? 'active' : ''}`}
              onClick={() => handleCurrencyChange('USDT')}
              disabled={balanceUsdt <= 0}
            >
              💵 USDT
            </button>
          </div>

          {/* Баланс копилки */}
          <div className="piggybank-balance-block">
            <div className="balance-row">
              <span className="balance-label">{t('piggyBankWithdraw.balance')} {selectedCurrency}:</span>
              <span className="balance-value">
                {selectedCurrency === 'USDT'
                  ? balanceUsdt.toFixed(2)
                  : currentBalance.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Преимущество SLOTS - не сбрасывает APR, но с вейджером */}
          {selectedCurrency === 'SLOTS' && (
            <div className="usdt-advantage-block" style={{ borderColor: 'rgba(245, 158, 11, 0.3)', background: 'rgba(245, 158, 11, 0.08)' }}>
              <span className="advantage-icon">🎰</span>
              <span className="advantage-text" style={{ color: '#fbbf24' }}>
                {t('piggyBankWithdraw.slotsAdvantage', 'APR НЕ сбрасывается! Средства будут заморожены до прокрутки x2 суммы в слотах.')}
              </span>
            </div>
          )}

          {/* Преимущество USDT - не сбрасывает APR */}
          {selectedCurrency === 'USDT' && (
            <div className="usdt-advantage-block">
              <span className="advantage-icon">✅</span>
              <span className="advantage-text">
                {t('piggyBankWithdraw.usdtAdvantage')}
              </span>
            </div>
          )}

          {/* Предупреждение об APR */}
          {hasAPRLoss && (
            <div className="apr-warning-block">
              <div className="apr-warning-header">
                <span className="warning-icon">⚠️</span>
                <div className="warning-text">
                  <div className="warning-title">
                    {t('piggyBankWithdraw.aprWarningTitle')}
                  </div>
                  <div className="warning-subtitle">
                    {t('piggyBankWithdraw.aprWarningSubtitle')}
                  </div>
                </div>
              </div>

              <div className="apr-loss-details">
                <div className="apr-loss-row">
                  <span className="loss-label">{t('piggyBankWithdraw.currentApr')}:</span>
                  <span className="loss-value current">{currentAPR.toFixed(2)}%</span>
                </div>
                <div className="apr-loss-row">
                  <span className="loss-label">{t('piggyBankWithdraw.afterWithdraw')}:</span>
                  <span className="loss-value after">{newAPR.toFixed(2)}%</span>
                </div>
                <div className="apr-loss-row highlight">
                  <span className="loss-label">{t('piggyBankWithdraw.growthLoss')}:</span>
                  <span className="loss-value lost">-{lossAPR.toFixed(2)}%</span>
                </div>
                {bonusAPR > 0 && (
                  <div className="apr-loss-row bonus-info">
                    <span className="loss-label">💎 {t('piggyBankWithdraw.boostersSaved')}:</span>
                    <span className="loss-value bonus">+{bonusAPR.toFixed(2)}%</span>
                  </div>
                )}
              </div>

              <div className="apr-warning-tip">
                💡 {t('piggyBankWithdraw.aprTip', { apr: accumulatedAPR.toFixed(2) })}
              </div>
            </div>
          )}

          {/* Выбор суммы вывода */}
          <div className="withdraw-amount-section">
            <label>{t('piggyBankWithdraw.withdrawAmount')}</label>

            <div className="withdraw-options">
              <button
                className={`option-btn ${withdrawAll ? 'active' : ''}`}
                onClick={handleWithdrawAll}
              >
                {t('piggyBankWithdraw.withdrawAll')}
              </button>
              <button
                className={`option-btn ${!withdrawAll ? 'active' : ''}`}
                onClick={() => {
                  setWithdrawAll(false);
                  setWithdrawAmount('');
                }}
              >
                {t('piggyBankWithdraw.specifyAmount')}
              </button>
            </div>

            {!withdrawAll && (
              <input
                type="text"
                value={withdrawAmount}
                onChange={(e) => handleAmountChange(e.target.value)}
                placeholder={t('piggyBankWithdraw.enterAmount', { currency: selectedCurrency })}
                className={`amount-input ${selectedCurrency === 'USDT' ? 'usdt-input' : ''}`}
              />
            )}

            {error && <div className="error-message">{error}</div>}
          </div>

          {/* Итоговая информация */}
          {finalAmount > 0 && !error && (
            <div className="withdraw-summary">
              <div className="summary-row">
                <span className="summary-label">{t('piggyBankWithdraw.toWithdraw')}:</span>
                <span className="summary-value">
                  {selectedCurrency === 'USDT'
                    ? finalAmount.toFixed(2)
                    : finalAmount.toLocaleString()} {selectedCurrency}
                </span>
              </div>
              <div className="summary-row">
                <span className="summary-label">{t('piggyBankWithdraw.willRemain')}:</span>
                <span className="summary-value">
                  {selectedCurrency === 'USDT'
                    ? (balanceUsdt - finalAmount).toFixed(2)
                    : (balance - finalAmount).toLocaleString()} {selectedCurrency}
                </span>
              </div>
            </div>
          )}

          {/* Условия вывода - разные для SPERM, SLOTS и USDT */}
          <div className="withdraw-conditions">
            <h3>📜 {t('piggyBankWithdraw.conditions')} {selectedCurrency === 'SLOTS' ? 'SPERM → Slots' : selectedCurrency}</h3>
            {selectedCurrency === 'SLOTS' ? (
              <ul>
                <li>✅ {t('piggyBankWithdraw.conditionSlotsNoApr', 'APR НЕ сбрасывается')}</li>
                <li>🔒 {t('piggyBankWithdraw.conditionSlotsWager', 'Средства заморожены до отыгрыша x2')}</li>
                <li>🎰 {t('piggyBankWithdraw.conditionSlotsPlay', 'Разморозка по мере игры в слоты')}</li>
                <li>💰 {t('piggyBankWithdraw.conditionSlotsWins', 'Выигрыши свободны сразу')}</li>
              </ul>
            ) : selectedCurrency === 'SPERM' ? (
              <ul>
                <li>❌ {t('piggyBankWithdraw.conditionNoReturn')}</li>
                <li>⚠️ {t('piggyBankWithdraw.conditionAprReset')}</li>
                <li>✅ {t('piggyBankWithdraw.conditionInstant')}</li>
                <li>💰 {t('piggyBankWithdraw.conditionContinue')}</li>
                <li>📈 {t('piggyBankWithdraw.conditionGrowth')}</li>
              </ul>
            ) : (
              <ul>
                <li>✅ {t('piggyBankWithdraw.conditionUsdtNoApr')}</li>
                <li>❌ {t('piggyBankWithdraw.conditionNoReturn')}</li>
                <li>✅ {t('piggyBankWithdraw.conditionUsdtInstant')}</li>
                <li>💵 {t('piggyBankWithdraw.conditionUsdtExternal')}</li>
              </ul>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose} disabled={isWithdrawingUsdt}>
            {t('common.cancel')}
          </button>
          <button
            className={`btn-confirm piggybank-withdraw-confirm ${hasAPRLoss ? 'with-warning' : ''} ${selectedCurrency === 'USDT' ? 'usdt-btn' : ''} ${selectedCurrency === 'SLOTS' ? 'slots-btn' : ''}`}
            onClick={handleConfirm}
            disabled={finalAmount <= 0 || !!error || isWithdrawingUsdt || isTransferringToSlots}
          >
            {isTransferringToSlots
              ? `⏳ ${t('piggyBankWithdraw.transferring', 'Переводим...')}`
              : isWithdrawingUsdt
                ? `⏳ ${t('piggyBankWithdraw.withdrawing')}`
                : selectedCurrency === 'SLOTS'
                  ? `🎰 ${t('piggyBankWithdraw.transferToSlots', 'Перевести в слоты')}`
                  : selectedCurrency === 'USDT'
                    ? `💵 ${t('piggyBankWithdraw.withdrawUsdt')}`
                    : hasAPRLoss
                      ? `⚠️ ${t('piggyBankWithdraw.withdrawAndReset')}`
                      : t('piggyBankWithdraw.withdrawToAccount')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PiggyBankWithdrawModal;
